import { CircleAlert, CircleCheck, CircleMinus, Loader2 } from 'lucide-react';

import type {
  RegistrationCodePreview,
  RegistrationCodePreviewCadence,
  RegistrationCodePreviewTier,
  RegistrationCodePreviewWindow,
  RegistrationCodeToastStatus,
} from '@Types/registrationCode';
import { formatCurrency } from '@Utils/utils';
import { FOREVER_WINDOW } from './inviteCodeForm';

const UNIT_NAMES: Record<string, { one: string; many: string }> = {
  DAY: { one: 'day', many: 'days' },
  MONTH: { one: 'month', many: 'months' },
  YEAR: { one: 'year', many: 'years' },
};

const pluralizeUnit = (amount: number, unit: string) => {
  const names = UNIT_NAMES[unit];
  if (!names) return unit.toLowerCase();
  return amount === 1 ? names.one : names.many;
};

const formatWindow = (window: RegistrationCodePreviewWindow | null) => {
  if (!window) return null;
  const isForever =
    window.unit === 'LIFETIME' ||
    (window.unit === FOREVER_WINDOW.unit &&
      window.amount >= FOREVER_WINDOW.amount);
  if (isForever) return 'forever';
  return `for ${window.amount} ${pluralizeUnit(window.amount, window.unit)} after joining`;
};

const formatCadence = ({ duration, unit }: RegistrationCodePreviewCadence) =>
  duration === 1
    ? `every ${pluralizeUnit(duration, unit)}`
    : `every ${duration} ${pluralizeUnit(duration, unit)}`;

function PriceLine({
  label,
  charge,
  listPrice,
  detail,
}: {
  label: string;
  charge: number;
  listPrice: number;
  detail?: string | null;
}) {
  const isDiscounted = charge !== listPrice;
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-zinc-600">{label}</span>
      <span className="text-right">
        <span className="font-medium">{formatCurrency(charge)}</span>
        {isDiscounted && (
          <span className="ml-1 text-zinc-400 line-through">
            {formatCurrency(listPrice)}
          </span>
        )}
        {detail && <span className="block text-xs text-zinc-500">{detail}</span>}
      </span>
    </div>
  );
}

function ToastStatusLine({
  label,
  status,
}: {
  label: string;
  status: RegistrationCodeToastStatus;
}) {
  if (status.status === 'not-needed') {
    return (
      <div className="flex items-start gap-2 text-sm text-zinc-500">
        <CircleMinus className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{label}: no Toast discount needed</span>
      </div>
    );
  }
  if (status.status === 'matched') {
    return (
      <div className="flex items-start gap-2 text-sm text-green-700">
        <CircleCheck className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          {label}: matches Toast discount “{status.discountName}”
        </span>
      </div>
    );
  }
  if (status.status === 'missing') {
    return (
      <div className="flex items-start gap-2 text-sm text-red-700">
        <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          {label}: no matching Toast discount. Create {status.expected} before
          saving.
        </span>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 text-sm text-amber-700">
      <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
      <span>
        {label}: Toast check unavailable ({status.reason})
      </span>
    </div>
  );
}

function TierPreview({ tier }: { tier: RegistrationCodePreviewTier }) {
  const { signup, renewal, toast } = tier;
  const renewalFee = renewal.membershipFee;
  const renewalWallet = renewal.wallet;

  const renewalFeeDetail = renewalFee
    ? [
        formatCadence(renewalFee.cadence),
        renewalFee.discountWindow &&
          `discount ${formatWindow(renewalFee.discountWindow)}`,
      ]
        .filter(Boolean)
        .join(', ')
    : null;

  const renewalWalletDetail = renewalWallet
    ? [
        formatCadence(renewalWallet.cadence),
        `${formatCurrency(renewalWallet.credit)} credited`,
        renewalWallet.discountWindow &&
          `discount ${formatWindow(renewalWallet.discountWindow)}`,
        renewalWallet.adjustmentWindow &&
          `credit adjustment ${formatWindow(renewalWallet.adjustmentWindow)}`,
      ]
        .filter(Boolean)
        .join(', ')
    : null;

  return (
    <div className="space-y-3 rounded-md border border-zinc-200 p-3">
      <div className="font-semibold">{tier.tierTitle}</div>

      <div className="space-y-1">
        <div className="text-xs font-bold text-zinc-500 uppercase">
          At signup
        </div>
        <PriceLine
          label="Membership fee"
          charge={signup.membershipFee.charge}
          listPrice={signup.membershipFee.listPrice}
        />
        {signup.wallet ? (
          <PriceLine
            label="Wallet deposit"
            charge={signup.wallet.charge}
            listPrice={signup.wallet.listPrice}
            detail={`${formatCurrency(signup.wallet.credit)} credited`}
          />
        ) : (
          <div className="text-sm text-zinc-500">No wallet deposit</div>
        )}
      </div>

      <div className="space-y-1">
        <div className="text-xs font-bold text-zinc-500 uppercase">
          At renewal
        </div>
        {renewalFee ? (
          <PriceLine
            label="Membership fee"
            charge={renewalFee.charge}
            listPrice={renewalFee.listPrice}
            detail={renewalFeeDetail}
          />
        ) : (
          <div className="text-sm text-zinc-500">No renewal fee</div>
        )}
        {renewalWallet ? (
          <PriceLine
            label="Wallet"
            charge={renewalWallet.charge}
            listPrice={renewalWallet.listPrice}
            detail={renewalWalletDetail}
          />
        ) : (
          <div className="text-sm text-zinc-500">No wallet renewal</div>
        )}
      </div>

      <div className="space-y-1">
        <div className="text-xs font-bold text-zinc-500 uppercase">Toast</div>
        <ToastStatusLine label="Membership fee" status={toast.subscription} />
        <ToastStatusLine label="Wallet deposit" status={toast.wallet} />
      </div>
    </div>
  );
}

export interface InviteCodePreviewPanelProps {
  preview?: RegistrationCodePreview;
  isUpdating: boolean;
  error: Error | null;
  isFormValid: boolean;
}

export default function InviteCodePreviewPanel({
  preview,
  isUpdating,
  error,
  isFormValid,
}: InviteCodePreviewPanelProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Preview</h3>
        {isUpdating && (
          <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
        )}
      </div>
      {!isFormValid && (
        <p className="text-sm text-zinc-500">
          Complete the form to update the preview.
        </p>
      )}
      {error && (
        <p className="text-sm text-red-700">
          Unable to load the preview: {error.message}
        </p>
      )}
      {preview?.tiers.length === 0 && (
        <p className="text-sm text-zinc-500">No tiers selected.</p>
      )}
      {preview?.tiers.map((tier) => (
        <TierPreview key={tier.tierId} tier={tier} />
      ))}
    </div>
  );
}
